import React, { useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';
import { createSelector } from '@reduxjs/toolkit';

import type { ISchedule } from '@/types/schedule';

import { formData, getTotalFromMinutes } from '@/helpers';
import { useAppDispatch, useAppSelector, useEditAvailability } from '@/hooks';
import { setSelectedSchedulesIds } from '@/store/slices/scheduleSlice';
import { Checkbox } from '@/components/common';
import { MAX_SCHEDULES_ACTIVE, SIDEBAR_DELAY } from '@/config/variables';
import { colors, otherColors } from '@/styles/colors';
import { Lock } from '@/assets/icons';
import { SidebarSchedules } from '@/store/enums';
import { selectScheduleColors, selectSelectedSchedulesIds } from '@/store/selectors/scheduleSelectors';
import { getWeeklyHours } from '@/helpers/schedules';

export interface ISchedulesPickerProps {
  schedulesList: ISchedule[];
  activeSchedulesLabel: string;
  hours: SidebarSchedules;
}

const schedulesPickerSelector = createSelector(
  selectScheduleColors,
  selectSelectedSchedulesIds,
  (scheduleColors, selectedSchedulesIds) => ({ scheduleColors, selectedSchedulesIds }),
);

const SchedulesPicker = ({ schedulesList, activeSchedulesLabel, hours }: ISchedulesPickerProps): React.ReactElement => {
  const dispatch = useAppDispatch();
  const { scheduleColors, selectedSchedulesIds } = useAppSelector(schedulesPickerSelector);
  const { findAvailability, isEditAvailability, getAvailabilityTotalMinutes } = useEditAvailability();
  const { t } = useTranslation();

  const {
    register,
    formState: { errors, touchedFields, isDirty },
  } = useForm();

  const formDataOptions = {
    register,
    errors,
    touchedFields,
  };

  const getTotalHours = (schedule: ISchedule, hoursType: SidebarSchedules) => {
    if (hoursType === SidebarSchedules.default) {
      return `${getWeeklyHours(schedule)}h`;
    }

    const availability = findAvailability(schedule.scheduleId);
    return `${getTotalFromMinutes(getAvailabilityTotalMinutes(availability?.intervals))
      .replace('min', 'm')
      .replace(' ', '')}`;
  };

  useEffect(() => {
    // we want to preselect schedules only if schedulesList has changed
    // but the checkboxes have to be untouched (isDirty)
    if (!isDirty && selectedSchedulesIds.length === 0 && schedulesList) {
      const startList = [...new Set(schedulesList.map(({ scheduleId }) => scheduleId))];
      dispatch(setSelectedSchedulesIds(startList.slice(0, MAX_SCHEDULES_ACTIVE)));
    }
  }, [dispatch, isDirty, schedulesList, selectedSchedulesIds.length]);

  const handleCheckboxClick = (event: React.ChangeEvent<HTMLInputElement>) => {
    const scheduleId = Number(event.target.id);

    if (selectedSchedulesIds.includes(scheduleId)) {
      dispatch(setSelectedSchedulesIds(selectedSchedulesIds.filter(item => item !== scheduleId)));
    } else {
      dispatch(setSelectedSchedulesIds([...selectedSchedulesIds, scheduleId]));
    }
  };

  return (
    <StyledContainer>
      <ActiveSchedulesLabel>{activeSchedulesLabel}</ActiveSchedulesLabel>
      {schedulesList.map((schedule, index) => (
        <ScheduleItem key={schedule.scheduleId}>
          <Checkbox
            showTooltip={true}
            name={schedule.scheduleId.toString()}
            onChange={handleCheckboxClick}
            label={`${index + 1}. ${schedule.clientName} - ${schedule.scheduleName} - ${getTotalHours(
              schedule,
              hours,
            )}`}
            checked={selectedSchedulesIds.includes(schedule.scheduleId)}
            color={scheduleColors ? colors[scheduleColors[schedule.scheduleId]]['c500'] : undefined}
            disabled={
              (!selectedSchedulesIds.includes(schedule.scheduleId) &&
                selectedSchedulesIds.length >= MAX_SCHEDULES_ACTIVE) ||
              Boolean(isEditAvailability)
            }
            {...formData(schedule.scheduleId.toString(), formDataOptions)}
          />
        </ScheduleItem>
      ))}
      {isEditAvailability && (
        <SchedulesDisabledLabel>{t`myAvailability.leftPanel.disabledSelection`}</SchedulesDisabledLabel>
      )}
    </StyledContainer>
  );
};

export { SchedulesPicker };

const StyledContainer = styled.div`
  display: flex;
  flex-direction: column;
`;

const SchedulesDisabledLabel = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  height: 36px;
  font-size: 12px;
  background-color: ${colors.gray.c50};
  color: ${otherColors.lxc.blue};
  pointer-events: none;
  transition: opacity ${SIDEBAR_DELAY / 1.5}ms ease-in, height ${SIDEBAR_DELAY / 1.5}ms ease-in;

  &::before {
    content: '';
    background-image: url(${Lock});
    height: 16px;
    width: 16px;
    margin-right: 5px;
    margin-top: -4px;
  }

  .collapsed & {
    opacity: 0;
    height: 0;
  }
`;

const ScheduleItem = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  padding: 12px 12px 12px 16px;

  left: 0;
  top: 57px;

  background-color: #fff;

  input[type='checkbox'] + label {
    font-size: 14px;
    margin-left: 13px !important;
    width: 100%;
    transition: opacity ${SIDEBAR_DELAY / 1.5}ms ease-in;

    .collapsed & {
      opacity: 0;
    }
  }

  .collapsed & {
    padding: 10px 12px;
  }
`;

const ActiveSchedulesLabel = styled.div`
  display: none;
  margin-top: 10px;
  text-align: center;

  .collapsed & {
    display: block;
  }
`;
